import { createContext, useContext, useMemo, use, type ReactNode } from 'react';
import { createApiClient, TIMEOUT_MS, type ApiClient, type Clock } from '@sl/core';
import type { DemoStorage } from '@sl/core/demo';
import type { AppConfig } from '../config';
import { APP_VERSION } from '../version';
import { browserConnectivity } from './connectivity';
import { withSessionIdentity } from './sessionIdentity';

// The ONE ApiClient of the app (ARCH §8.2, §9.1). `createApiClient` is async — the demo server is a dynamic
// import — so the provider holds the promise and `useApiClient()` suspends until it settles. The welcome
// route never asks for the client, so a visitor who stays there never downloads the demo chunk; the Home
// route is the first to await it. The client is created once per provider and never re-created.

export type ApiClientSource = ApiClient | Promise<ApiClient>;

const ApiClientContext = createContext<Promise<ApiClient> | null>(null);

/**
 * What `X-Client-Version` carries. The app's real version (`apps/web/package.json`, substituted at build
 * time — see `app/version.ts`), or `0.0.0` when this build has none, which is what an unversioned client
 * has always meant on the wire. The About row says "development build" instead; a header is not prose.
 */
export const CLIENT_VERSION = APP_VERSION ?? '0.0.0';

/**
 * `localStorage` for the demo server (T-021 hand-over (2)). Without it the demo keeps its state in memory and
 * "Reset demo", the View-only preview and the saved preferences last exactly one page load. Every key is
 * namespaced `sl.demo.<datasetVersion>.<accountId>.<name>`, and "Reset demo" clears that namespace only.
 * Private browsing can refuse either call, so every one of them degrades to "nothing stored".
 */
export function browserDemoStorage(): DemoStorage {
  const store = (): Storage | null => {
    try {
      return window.localStorage;
    } catch {
      return null; // storage blocked: the demo still opens, it just forgets
    }
  };
  return {
    get: async (key) => store()?.getItem(key) ?? null,
    set: async (key, value) => {
      try {
        store()?.setItem(key, value);
      } catch {
        /* quota or private mode — the demo is not worth an error */
      }
    },
    remove: async (key) => {
      try {
        store()?.removeItem(key);
      } catch {
        /* as above */
      }
    },
    keys: async () => {
      const storage = store();
      return storage ? Object.keys(storage) : [];
    },
  };
}

/** Demo by default (REQ-001 AC1): no backend, no network. */
export async function createAppApiClient(config: AppConfig, clock: Clock): Promise<ApiClient> {
  if (config.transport === 'http') {
    return withSessionIdentity(
      await createApiClient({
        transport: 'http',
        baseUrl: config.apiBaseUrl,
        client: 'web',
        clientVersion: CLIENT_VERSION,
        timeoutMs: TIMEOUT_MS.web,
        connectivity: browserConnectivity(),
      }),
    );
  }
  // T-282 (D-362) — `?dataset=live`: the real account's dataset, pulled by `tools/meta-pull.mjs` and served
  // beside the app under `live/`, handed to the SAME demo server, which serves it as a live, view-only
  // account (`mode: 'live_view_only'`, nothing publishes). No QA switch applies to it — what it shows is what
  // the pull read — so none is passed. The loader fetches two static files from this origin and nothing else.
  //
  // T-283: WHICH live account comes off the config too (`?account=<key>`, or the remembered choice) — the
  // switch in the shell is what writes it, and an unknown key falls back to the first entry of the index, so a
  // re-pull under a new salt opens the account rather than a dead end.
  if (config.dataset === 'live') {
    const { loadLiveDataset } = await import('./liveDataset');
    const { dataset } = await loadLiveDataset({ accountKey: config.liveAccountKey });
    return countCalls(
      await createApiClient({
        transport: 'demo',
        clock,
        storage: browserDemoStorage(),
        dataset,
        // T-303 / 0.3.10 (D-552, D-594, API §8.4 (j)) — WITHOUT THIS LINE EVERY STILL IS A PLACEHOLDER. The
        // dataset carries each picture as a RELATIVE `live/media/<file>` ref, and `resolveMedia` resolves it
        // against this base and refuses every absolute URL, Meta's own CDN included. `document.baseURI` is the
        // same base `loadLiveDataset` just fetched `live/index.json` from, so a picture is read from the
        // product's own origin and nowhere else — the app makes no request to any Meta host for media.
        mediaBase: document.baseURI,
      }),
    );
  }
  // The QA switches (`?demoScenario=`, `?demoFault=`, `?demoPreview=view-only`, `?demoConnection=`) are
  // parsed inside the demo chunk so that an `http` build never imports `@sl/core/demo` at all (ARCH §8.1);
  // `?demoNow=` is already in `config` because the clock is built before the client.
  //
  // **T-076-W7**: the switches come off `config.qa` — filtered to the five names, read ONCE, from the
  // location `readAppConfig` was given. This used to re-read `window.location` here, so a test that passed a
  // synthetic location got the real address at the client and every stray query parameter on the page was
  // handed to the demo server.
  const { parseDemoSwitches } = await import('@sl/core/demo');
  const switches = parseDemoSwitches(config.qa);
  const client = await createApiClient({
    transport: 'demo',
    clock,
    storage: browserDemoStorage(),
    scenario: switches.scenario,
    faults: switches.faults,
    offline: switches.offline,
    // `?demoConnection=` (T-048 ask (1)): the demo SERVER reports the state now, so this is one option
    // rather than a client-side wrapper, and mobile and the C# Sandbox get the same switch (T-237).
    connection: switches.connection,
    viewOnlyPreview: switches.viewOnlyPreview,
    // `?demoFault=backtest:slow[:ms]` (T-166) and `?demoFault=act:conflict` (T-167): the rule builder's
    // slow back-test and the alert action's `409 value_moved`, both reachable in a built demo.
    backtestSlowMs: switches.backtestSlowMs,
    actConflict: switches.actConflict,
    // `?demoFault=period:missing` (T-364, D-892): every period-keyed answer without its echo, so the declared
    // error state is reachable by address rather than only by a test.
    omitPeriod: switches.omitPeriod,
    // `?demoFault=picker:wide` (T-368, D-1245): the picker's three caps, reachable on the demo itself.
    widePicker: switches.widePicker,
  });
  return countCalls(config.apiBuild ? withSessionIdentity(client) : client);
}

/** What the demo build publishes to the page for a test to read (`window.__slDemoCalls`). */
declare global {
  var __slDemoCalls: Record<string, number> | undefined;
}

/**
 * A per-method call count on `window`, in the DEMO build only (QA's T-152 ask (4)).
 *
 * **Why it earns its place.** "No action flow ever publishes" is the standing promise of the whole Act
 * slice, and without this it can only be tested as the ABSENCE of a live-region sentence — a check that
 * passes when the screen is silent for any reason at all, including a broken one. With it, the assertion is
 * `__slDemoCalls.publishChanges === 0`: a count, about the thing the promise is about.
 *
 * **It cannot reach a real account.** This wrapper is created after the `http` branch has already returned,
 * so an `http` build never calls it. All sample campaign methods stay on the in-memory server; an API build
 * may separately read the signed-in identity to label the user row. This counter stores no bodies,
 * arguments or ids (HARD RULE 2), so nothing it exposes could be sensitive even if a build did ship it.
 *
 * Said plainly, because the ask was for one line: it is about a dozen, and this comment is most of them.
 */
function countCalls(client: ApiClient): ApiClient {
  if (typeof globalThis.window === 'undefined') return client;
  const counts: Record<string, number> = {};
  globalThis.__slDemoCalls = counts;
  return new Proxy(client, {
    get(target, key, receiver) {
      const value = Reflect.get(target, key, receiver);
      if (typeof value !== 'function' || typeof key !== 'string') return value;
      return (...args: unknown[]) => {
        counts[key] = (counts[key] ?? 0) + 1;
        return (value as (...a: unknown[]) => unknown).apply(target, args);
      };
    },
  });
}

export function ApiClientProvider({
  client,
  children,
}: {
  /** A ready client or the promise of one — tests pass a fake, `App` passes `createAppApiClient(...)`. */
  client: ApiClientSource;
  children: ReactNode;
}) {
  const promise = useMemo(() => Promise.resolve(client), [client]);
  return <ApiClientContext.Provider value={promise}>{children}</ApiClientContext.Provider>;
}

/** Suspends until the client exists. Wrap the caller in a `<Suspense>` with a loading state. */
export function useApiClient(): ApiClient {
  const promise = useContext(ApiClientContext);
  if (!promise) throw new Error('useApiClient() needs an <ApiClientProvider> above it.');
  return use(promise);
}
